import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LineChart as LineChartIcon,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { PageHeader } from '../components/ui/PageHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorAlert } from '../components/ui/ErrorAlert';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';
import {
  createTracker,
  deleteTracker,
  listTrackers,
  trackerHistory,
  type RankTracker,
} from '../api/rankTracker';

const CADENCES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
] as const;

function errMessage(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.message : fallback;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function RankTrackerPage() {
  const { token } = useAuth();
  const [historyFor, setHistoryFor] = useState<RankTracker | null>(null);

  const trackersQuery = useQuery({
    queryKey: ['rank-tracker'],
    queryFn: () => listTrackers(token),
  });

  const trackers = trackersQuery.data ?? [];

  return (
    <>
      <PageHeader
        icon={LineChartIcon}
        title="Rank Tracker"
        description="Track pin position for a keyword over time — configurable polling frequency."
      />

      <CreateTrackerForm token={token} />

      {trackersQuery.isError && (
        <ErrorAlert message={errMessage(trackersQuery.error, 'Failed to load trackers.')} />
      )}

      {trackersQuery.isPending && (
        <div className="py-16 text-center text-sm text-ink-400">Loading trackers…</div>
      )}

      {trackersQuery.isSuccess && trackers.length === 0 && (
        <EmptyState
          icon={LineChartIcon}
          title="No trackers yet"
          description="Add a pin ID and the keyword you want to rank for. We'll search Pinterest on your cadence and record the pin's position each time."
        />
      )}

      {trackers.length > 0 && (
        <div className="rounded-lg border border-ink-200 bg-white shadow-sm">
          <div className="border-b border-ink-100 px-6 py-4">
            <h2 className="text-sm font-semibold text-ink-900">
              {trackers.length} tracker{trackers.length === 1 ? '' : 's'}
            </h2>
            <p className="text-xs text-ink-500">
              Position is captured from live Pinterest search results — real data.
            </p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
                <th className="py-3 pl-6 text-left">Keyword</th>
                <th className="py-3 text-left">Pin</th>
                <th className="py-3 text-left">Cadence</th>
                <th className="py-3 text-right">Position</th>
                <th className="py-3 pr-2 text-right">Last checked</th>
                <th className="w-24 py-3 pr-6"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {trackers.map((t) => (
                <TrackerRow
                  key={t.id}
                  tracker={t}
                  token={token}
                  onOpenHistory={() => setHistoryFor(t)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {historyFor && (
        <HistoryDrawer
          tracker={historyFor}
          token={token}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </>
  );
}

function CreateTrackerForm({ token }: { token: string | null }) {
  const qc = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [pinId, setPinId] = useState('');
  const [cadence, setCadence] = useState<RankTracker['cadence']>('daily');

  const mut = useMutation({
    mutationFn: () =>
      createTracker({ pinId: pinId.trim(), keyword: keyword.trim(), cadence }, token),
    onSuccess: () => {
      setKeyword('');
      setPinId('');
      qc.invalidateQueries({ queryKey: ['rank-tracker'] });
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!keyword.trim() || !pinId.trim()) return;
    mut.mutate();
  }

  return (
    <div className="mb-6">
      <form
        onSubmit={onSubmit}
        className="flex flex-wrap gap-2 rounded-lg border border-ink-200 bg-white p-2 shadow-sm"
      >
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder='Keyword — e.g. "dog training"'
          className="min-w-48 flex-1 rounded-md px-3 py-2 text-sm focus:outline-none"
        />
        <input
          type="text"
          value={pinId}
          onChange={(e) => setPinId(e.target.value)}
          placeholder="Pinterest pin ID — e.g. 1234567890"
          className="min-w-48 flex-1 rounded-md px-3 py-2 font-mono text-sm focus:outline-none"
        />
        <select
          value={cadence}
          onChange={(e) => setCadence(e.target.value as RankTracker['cadence'])}
          className="rounded-md border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700 focus:border-brand-500 focus:outline-none"
        >
          {CADENCES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={mut.isPending || !keyword.trim() || !pinId.trim()}
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {mut.isPending ? 'Adding…' : 'Track'}
        </button>
      </form>
      {mut.isError && (
        <div className="mt-2">
          <ErrorAlert message={errMessage(mut.error, 'Could not create tracker.')} />
        </div>
      )}
      <p className="mt-2 text-xs text-ink-500">
        The pin ID is the number in the pin's URL: pinterest.com/pin/
        <span className="font-mono">1234567890</span>. Each keyword/pin pair is checked on its
        cadence.
      </p>
    </div>
  );
}

function TrackerRow({
  tracker,
  token,
  onOpenHistory,
}: {
  tracker: RankTracker;
  token: string | null;
  onOpenHistory: () => void;
}) {
  const qc = useQueryClient();
  const latest = tracker.snapshots[0] ?? null;

  const del = useMutation({
    mutationFn: () => deleteTracker(tracker.id, token),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rank-tracker'] }),
  });

  return (
    <tr className="hover:bg-ink-50">
      <td className="py-3 pl-6 pr-4 font-medium text-ink-900">{tracker.keyword}</td>
      <td className="py-3 pr-4">
        <a
          href={`https://www.pinterest.com/pin/${tracker.pinId}/`}
          target="_blank"
          rel="noreferrer"
          className="font-mono text-xs text-ink-500 hover:text-brand-500 hover:underline"
        >
          {tracker.pinId}
        </a>
      </td>
      <td className="py-3 pr-4">
        <span className="inline-flex rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-700">
          {tracker.cadence}
        </span>
      </td>
      <td className="py-3 pr-4 text-right tabular-nums">
        <PositionBadge latest={latest} />
      </td>
      <td className="py-3 pr-2 text-right text-xs text-ink-500">
        {latest ? fmtDate(latest.capturedAt) : '—'}
      </td>
      <td className="py-3 pr-6">
        <div className="flex justify-end gap-1">
          <button
            type="button"
            onClick={onOpenHistory}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
            title="Position history"
          >
            <LineChartIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Stop tracking "${tracker.keyword}"? History will be deleted.`)) {
                del.mutate();
              }
            }}
            disabled={del.isPending}
            className="rounded-md p-1 text-ink-500 hover:bg-brand-50 hover:text-brand-600 disabled:opacity-50"
            title="Delete tracker"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function PositionBadge({
  latest,
}: {
  latest: RankTracker['snapshots'][number] | null;
}) {
  if (!latest) {
    return <span className="text-xs text-ink-400">awaiting first check</span>;
  }
  if (latest.position === null) {
    return (
      <span className="text-xs text-ink-500">
        not in top {latest.totalResults ?? 100}
      </span>
    );
  }
  const top10 = latest.position <= 10;
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
        top10 ? 'bg-emerald-50 text-emerald-700' : 'bg-ink-100 text-ink-700'
      }`}
    >
      #{latest.position}
    </span>
  );
}

function HistoryDrawer({
  tracker,
  token,
  onClose,
}: {
  tracker: RankTracker;
  token: string | null;
  onClose: () => void;
}) {
  const historyQuery = useQuery({
    queryKey: ['rank-tracker', tracker.id, 'history'],
    queryFn: () => trackerHistory(tracker.id, token),
  });

  // API returns newest-first; chart wants chronological. Missing (null) positions
  // become gaps in the line rather than fake values.
  const data = useMemo(
    () =>
      (historyQuery.data ?? [])
        .slice()
        .reverse()
        .map((s) => ({
          date: fmtDate(s.capturedAt),
          position: s.position,
        })),
    [historyQuery.data],
  );

  const positions = data
    .map((d) => d.position)
    .filter((p): p is number => p !== null);
  const best = positions.length ? Math.min(...positions) : null;
  const latest = historyQuery.data?.[0]?.position ?? null;

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-40 flex justify-end bg-ink-900/40"
      aria-modal="true"
      role="dialog"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="h-full w-full max-w-lg overflow-y-auto bg-white p-6 shadow-lg"
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-ink-400">Position history</p>
            <h2 className="mt-1 text-lg font-semibold text-ink-900">{tracker.keyword}</h2>
            <p className="mt-0.5 font-mono text-xs text-ink-500">pin {tracker.pinId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <dl className="grid grid-cols-3 gap-4 rounded-md border border-ink-100 bg-ink-50 p-4 text-sm">
          <div>
            <dt className="text-xs uppercase text-ink-400">Latest</dt>
            <dd className="mt-1 font-semibold text-ink-900">
              {latest === null ? '—' : `#${latest}`}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-ink-400">Best</dt>
            <dd className="mt-1 font-semibold text-ink-900">{best === null ? '—' : `#${best}`}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-ink-400">Checks</dt>
            <dd className="mt-1 font-semibold text-ink-900">{data.length}</dd>
          </div>
        </dl>

        <div className="mt-6">
          <h3 className="text-sm font-semibold text-ink-900">Rank over time</h3>
          <p className="mt-0.5 text-xs text-ink-500">
            Lower is better — #1 is the top of Pinterest search. Gaps mean the pin wasn't in the
            scanned results for that check.
          </p>
          {historyQuery.isPending ? (
            <div className="mt-4 flex h-56 items-center justify-center text-sm text-ink-400">
              Loading history…
            </div>
          ) : historyQuery.isError ? (
            <div className="mt-4">
              <ErrorAlert
                message={errMessage(historyQuery.error, 'Failed to load history.')}
              />
            </div>
          ) : data.length === 0 ? (
            <div className="mt-4 flex h-56 items-center justify-center rounded-md border border-dashed border-ink-200 text-sm text-ink-500">
              No checks recorded yet — the first one runs on the tracker's cadence.
            </div>
          ) : (
            <div className="mt-4 h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    minTickGap={24}
                  />
                  <YAxis
                    reversed
                    domain={[1, 'dataMax']}
                    allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v: number) => `#${v}`}
                  />
                  <Tooltip
                    formatter={(v: number) => [`#${v}`, 'Position']}
                    contentStyle={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="position"
                    stroke="#6d5efc"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
